import { useCallback, useMemo } from 'react';
import type { CSSProperties } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { Box, HStack } from '@cbhq/cds-web/layout';
import { Spinner } from '@cbhq/cds-web/loaders/Spinner';
import { FullscreenModal } from '@cbhq/cds-web/overlays/modal/FullscreenModal';
import { Portal } from '@cbhq/cds-web/overlays/Portal';
import { tooltipContainerId } from '@cbhq/cds-web/overlays/PortalProvider';

import type { StepUpTwoFactorProps } from '../../../components/StepUpTwoFactor';
import { StepUpTwoFactor } from '../../../components/StepUpTwoFactor';

const messages = defineMessages({
  accessibilityLabel: {
    id: 'Team.stepUpTwoFactorModal.title',
    defaultMessage: 'Verify it’s you',
    description:
      'Accessibility label for the modal that runs the two-factor challenge before a member change',
  },
  close: {
    id: 'Team.stepUpTwoFactorModal.close',
    defaultMessage: 'Cancel verification',
    description: 'Accessibility label for closing the two-factor verification modal',
  },
});

const CONTENT_WIDTH = 448;

// FullscreenModal's content row caps primaryContent at 800px but never centers
// that column (justify-content defaults to flex-start), and reserves a left
// margin at wider breakpoints to make room for a secondaryContent column we
// don't have — so without secondaryContent, the capped primaryContent column
// sits pinned to the left of the viewport. Centering and zeroing that margin
// here fixes it.
const CONTENT_STYLE: CSSProperties = { margin: 0, justifyContent: 'center' };

// Centers the content within its (possibly wider) primaryContent column.
const CENTER_STYLE: CSSProperties = { marginInline: 'auto' };

export type StepUpTwoFactorModalProps = {
  visible: boolean;
  onSuccess: (proofToken: string) => void;
  onCancel: () => void;
} & Pick<StepUpTwoFactorProps, 'target' | 'mockConfig'>;

// Overlays StepUpTwoFactor on the confirm step so cancelling preserves the form.
export function StepUpTwoFactorModal({
  visible,
  onSuccess,
  onCancel,
  target,
  mockConfig,
}: StepUpTwoFactorModalProps) {
  const { formatMessage } = useIntl();

  const handleSuccess = useCallback(
    (proofToken: string) => {
      onSuccess(proofToken);
    },
    [onSuccess],
  );

  const primaryContent = useMemo(
    () => (
      <Box width="100%" maxWidth={CONTENT_WIDTH} style={CENTER_STYLE}>
        <StepUpTwoFactor
          loader={
            <HStack justifyContent="center" padding={6}>
              <Spinner size={3} />
            </HStack>
          }
          onSuccess={handleSuccess}
          onCancel={onCancel}
          target={target}
          mockConfig={mockConfig}
        />
      </Box>
    ),
    [handleSuccess, mockConfig, onCancel, target],
  );

  // Needs a z-index above Tray (4) to stack over an open tray, but below Toast (6) so
  // toasts stay visible while this is open — tooltipContainerId (5) is the only
  // container in that gap. It also backs Popover (Dropdown/Select/Combobox/menus), so
  // in theory something could collide with us there, but those close on blur/outside
  // click the moment our focus trap steals focus, unlike a Tray, which stays open.
  //
  // Always rendered so FullscreenModal's own AnimatePresence can play its exit
  // transition — an early return on `!visible` would unmount before it runs.
  return (
    <Portal containerId={tooltipContainerId}>
      <FullscreenModal
        visible={visible}
        disablePortal
        onRequestClose={onCancel}
        accessibilityLabel={formatMessage(messages.accessibilityLabel)}
        closeAccessibilityLabel={formatMessage(messages.close)}
        primaryContent={primaryContent}
        contentStyle={CONTENT_STYLE}
        hideDivider
      />
    </Portal>
  );
}
